// Configuración para la API de proyectos con manejo de errores mejorado

const API_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 segundo
  timeout: 10000, // 10 segundos
};

/**
 * Realiza una petición HTTP con reintentos automáticos
 * @param {string} url - URL de la API
 * @param {object} options - Opciones de fetch
 * @param {number} retries - Número de reintentos restantes
 * @returns {Promise<Response>} - Respuesta de la API
 */
export const fetchWithRetry = async (url, options = {}, retries = API_CONFIG.maxRetries) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (retries > 0 && !controller.signal.aborted) {
      console.warn(`Reintentando petición... Intentos restantes: ${retries - 1}`);
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
      return fetchWithRetry(url, options, retries - 1);
    }
    
    throw error;
  }
};

/**
 * Verifica si la API está disponible
 * @returns {Promise<boolean>} - true si la API está disponible
 */
export const checkApiAvailability = async () => {
  try {
    const apiKey = import.meta.env.VITE_API_KEY;
    
    if (!apiKey) {
      console.error('Variables de entorno de API no configuradas');
      return false;
    }
    
    // En desarrollo, usar el proxy de Vite
    const isDevelopment = import.meta.env.DEV;
    const apiUrl = isDevelopment 
      ? `/api/proyectos?apikey=${apiKey}`
      : `${import.meta.env.VITE_API_URL}?apikey=${apiKey}`;
    
    // Hacer una petición simple para verificar conectividad
    const response = await fetchWithRetry(apiUrl, {}, 1);
    return response.ok;
  } catch (error) {
    console.warn('API no disponible:', error.message);
    return false;
  }
};

/**
 * Datos de fallback para cuando la API no esté disponible
 * toca cambiarlo a que diga lista no disponible
 */
export const getFallbackProjects = () => {
  return [
    {
      id: 'FALLBACK_001',
      name: 'Proyecto de Prueba 1',
      email: 'test1@iets.org.co',
      Product: {
        product_0: 'Producto A',
        product_1: 'Producto B'
      }
    },
    {
      id: 'FALLBACK_002', 
      name: 'Proyecto de Prueba 2',
      email: 'test2@iets.org.co',
      Product: {
        product_0: 'Producto C'
      }
    }
  ];
};

export default API_CONFIG;